import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../../contexts/AuthContext';
import { FileApi } from '../../../services/ApiService';
import { syllabusScanService } from '../../../services/SyllabusScanService';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import { InviteApi } from '../../../services/api/InviteApi';
import CourseMatchBanner from '../../../components/onboarding/CourseMatchBanner';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

/* ── Manual Course Entry Sub-Component ─────────────────────────────── */
function ManualCourseForm({ onSubmit, onBack, onFinish, courseCount, addedCourses, onJoinedSharedCourse }) {
  const [courseName, setCourseName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [instructor, setInstructor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [confirmedDone, setConfirmedDone] = useState(false);

  const canContinue = courseName.trim() && startDate && endDate;
  const currentCourseNum = addedCourses.length + 1;
  const needsMore = currentCourseNum < courseCount && !confirmedDone;
  const hasAdded = addedCourses.length > 0;

  const resetForm = () => {
    setCourseName('');
    setCourseCode('');
    setInstructor('');
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setLocation('');
  };

  const handleAddCourse = () => {
    if (!canContinue) return;
    onSubmit({
      courseName: courseName.trim(),
      courseCode: courseCode.trim(),
      instructor: instructor.trim(),
      startDate,
      endDate,
      meetingStartTime: startTime,
      meetingEndTime: endTime,
      location: location.trim(),
    });
    resetForm();
  };

  const handleFinish = () => {
    // If there's a filled-out form, include it in the final submission
    if (canContinue) {
      const lastEntry = {
        courseName: courseName.trim(),
        courseCode: courseCode.trim(),
        instructor: instructor.trim(),
        startDate,
        endDate,
        meetingStartTime: startTime,
        meetingEndTime: endTime,
        location: location.trim(),
      };
      onFinish(lastEntry);
    } else {
      onFinish(null);
    }
  };

  const handleNoMore = () => {
    setConfirmedDone(true);
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'manual_done_early',
      category: 'onboarding',
      label: 'syllabus-upload',
      metadata: { addedCourses: addedCourses.length, courseCount },
    });
  };

  return (
    <div className="onboarding-card ob-upload-card ob-manual-card">
      {/* Header */}
      <div className="onboarding-card-header ob-upload-header">
        <h2>
          {hasAdded
            ? `Add course ${currentCourseNum}`
            : 'Set up your course manually'}
        </h2>
        <p className="ob-upload-subtitle">
          {hasAdded
            ? `You've added ${addedCourses.length} of ${courseCount} courses. Fill in the next one below.`
            : "No worries! Enter your course details below and we'll help you get organized."}
        </p>
      </div>

      {/* Added courses summary */}
      {hasAdded && (
        <div className="ob-manual-added-list">
          {addedCourses.map((c, i) => (
            <div key={i} className="ob-manual-added-item">
              <span className="ob-manual-added-check">✓</span>
              <span className="ob-manual-added-name">{c.courseName}</span>
              {c.courseCode && <span className="ob-manual-added-code">{c.courseCode}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Reminder when they've added some but have more to go */}
      {hasAdded && needsMore && !canContinue && (
        <div className="ob-upload-reminder">
          <div className="ob-upload-reminder-text">
            <span className="ob-upload-reminder-primary">
              You said you're taking <strong>{courseCount}</strong>{' '}
              {courseCount === 1 ? 'course' : 'courses'}.
            </span>
            <span className="ob-upload-reminder-secondary">
              You've added <strong>{addedCourses.length}</strong> so far.
              {' '}Add more courses below, or continue if that's all you have.
            </span>
          </div>
          <div className="ob-upload-reminder-actions">
            <button
              type="button"
              className="ob-upload-reminder-btn ob-upload-reminder-btn--text"
              onClick={handleNoMore}
            >
              That's all I have
            </button>
          </div>
        </div>
      )}

      {/* Form — show if they haven't confirmed done, or if they have no courses yet */}
      {(!hasAdded || !confirmedDone) && (
        <div className="ob-manual-form">
          {/* Course name & code row */}
          <div className="ob-manual-row ob-manual-row--2col">
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-course-name">
                Course Name <span className="ob-manual-required">*</span>
              </label>
              <input
                id="ob-course-name"
                type="text"
                className="ob-manual-input"
                placeholder="e.g. Introduction to Psychology"
                value={courseName}
                onChange={e => setCourseName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-course-code">Course Code</label>
              <input
                id="ob-course-code"
                type="text"
                className="ob-manual-input"
                placeholder="e.g. PSY 101"
                value={courseCode}
                onChange={e => setCourseCode(e.target.value)}
              />
            </div>
          </div>

          {/* Instructor & location row */}
          <div className="ob-manual-row ob-manual-row--2col">
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-instructor">Instructor</label>
              <input
                id="ob-instructor"
                type="text"
                className="ob-manual-input"
                placeholder="e.g. Dr. Smith"
                value={instructor}
                onChange={e => setInstructor(e.target.value)}
              />
            </div>
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-location">Location</label>
              <input
                id="ob-location"
                type="text"
                className="ob-manual-input"
                placeholder="e.g. Room 201, Science Hall"
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>
          </div>

          {/* Shared course match suggestions */}
          <CourseMatchBanner
            courseName={courseName}
            instructor={instructor}
            onJoined={onJoinedSharedCourse}
          />

          {/* Divider */}
          <div className="ob-manual-divider">
            <span className="ob-manual-divider-line" />
            <span className="ob-manual-divider-text">Schedule</span>
            <span className="ob-manual-divider-line" />
          </div>

          {/* Date range row */}
          <div className="ob-manual-row ob-manual-row--2col">
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-start-date">
                Start Date <span className="ob-manual-required">*</span>
              </label>
              <input
                id="ob-start-date"
                type="date"
                className="ob-manual-input"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-end-date">
                End Date <span className="ob-manual-required">*</span>
              </label>
              <input
                id="ob-end-date"
                type="date"
                className="ob-manual-input"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Meeting times row */}
          <div className="ob-manual-row ob-manual-row--2col">
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-start-time">Start Time</label>
              <input
                id="ob-start-time"
                type="time"
                className="ob-manual-input"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
              />
            </div>
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-end-time">End Time</label>
              <input
                id="ob-end-time"
                type="time"
                className="ob-manual-input"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
              />
            </div>
          </div>

          {/* Helpful hint */}
          <p className="ob-manual-hint">
            You can always add assignments, class sessions, and more details in the next step.
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="onboarding-actions ob-upload-actions">
        <button className="onboarding-btn secondary" onClick={onBack}>
          ← Back
        </button>
        <div className="ob-upload-actions-right">
          {/* "Add & Next Course" when there are more courses to add */}
          {needsMore && !confirmedDone && (
            <button
              className={`onboarding-btn secondary${canContinue ? '' : ' ob-btn--muted'}`}
              onClick={handleAddCourse}
              disabled={!canContinue}
            >
              Save & Add Next Course
            </button>
          )}
          {/* Final "Continue" — either finishes adding or just continues with 1 */}
          <button
            className={`onboarding-btn primary${(hasAdded || canContinue) ? '' : ' ob-btn--muted'}`}
            onClick={handleFinish}
            disabled={!hasAdded && !canContinue}
          >
            Continue to Course Setup →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Staged system messages ── */
const SYSTEM_MESSAGES = [
  'Reading your syllabi…',
  'Organizing your semester…',
];

/* ── Floating particles (memoised once) ── */
const PARTICLES = Array.from({ length: 10 }, (_, i) => ({
  id: i,
  left: `${8 + Math.random() * 84}%`,
  top: `${8 + Math.random() * 84}%`,
  size: 2 + Math.random() * 3.5,
  dur: 5 + Math.random() * 5,
  delay: Math.random() * 4,
}));

/* ── Upload doc icon (inline SVG) ── */
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" aria-hidden="true" className="syl-upload-icon">
    <rect x="12" y="4" width="40" height="52" rx="5" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.4" />
    <path d="M38 4L38 16L52 16" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" strokeLinejoin="round" />
    <line x1="22" y1="26" x2="42" y2="26" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.25" />
    <line x1="22" y1="32" x2="38" y2="32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.25" />
    <line x1="32" y1="60" x2="32" y2="48" stroke="var(--color-primary, #6366f1)" strokeWidth="2.5" strokeLinecap="round" />
    <polyline points="26,52 32,46 38,52" stroke="var(--color-primary, #6366f1)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/* ── File icon ── */
const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

/**
 * Step 3 — Syllabus Upload — Scout-driven experience
 *
 * Two-column: Scout (left) + upload surface (right).
 * Files start scanning immediately. Auto-advances after upload completes.
 * Staged system messages make the wait feel active.
 */
export default function SyllabusUploadStep({ data, updateData, goNext, goBack, goToStep }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [, setConfirmedDone] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState(null);
  const [joinPreview, setJoinPreview] = useState(null);
  const [joining, setJoining] = useState(false);
  const [manualCourseEntries, setManualCourseEntries] = useState([]);
  const [systemMsgIdx, setSystemMsgIdx] = useState(0);
  const [autoAdvancing, setAutoAdvancing] = useState(false);
  const advanceTimerRef = useRef(null);

  // Derived state
  const uploadedFiles = useMemo(() => data.syllabusFiles || [], [data.syllabusFiles]);
  const uploadedIds = useMemo(() => data.syllabusDocumentIds || [], [data.syllabusDocumentIds]);
  const courseCount = data.courseCount || 1;
  const hasUploads = uploadedFiles.length > 0;
  const allMatched = hasUploads && uploadedFiles.length >= courseCount;
  const hasMismatch = hasUploads && uploadedFiles.length < courseCount;

  // Cycle system messages while uploading or auto-advancing
  useEffect(() => {
    if (!uploading && !autoAdvancing) return;
    setSystemMsgIdx(0);
    const interval = setInterval(() => {
      setSystemMsgIdx(prev => (prev + 1) % SYSTEM_MESSAGES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [uploading, autoAdvancing]);

  // Auto-advance ONLY when uploads match or exceed courseCount
  const autoAdvancingRef = useRef(false);
  useEffect(() => {
    if (allMatched && !uploading && !autoAdvancingRef.current) {
      autoAdvancingRef.current = true;
      setAutoAdvancing(true);
      advanceTimerRef.current = setTimeout(() => {
        goNext();
      }, 2800);
    } else if (!allMatched && autoAdvancingRef.current) {
      // Files were removed — cancel pending advance
      clearTimeout(advanceTimerRef.current);
      autoAdvancingRef.current = false;
      setAutoAdvancing(false);
    }
  }, [allMatched, uploading, goNext]);

  // Clean up timer on unmount
  useEffect(() => () => clearTimeout(advanceTimerRef.current), []);

  // ── Upload handler ──
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setError(null);
    setUploading(true);

    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'upload_attempt',
      category: 'onboarding',
      label: 'syllabus-upload',
      metadata: {
        fileCount: acceptedFiles.length,
        fileTypes: [...new Set(acceptedFiles.map(f => f.type || 'unknown'))],
        totalSizeBytes: acceptedFiles.reduce((s, f) => s + f.size, 0),
      },
    });

    try {
      const uploadPromises = acceptedFiles.map((file) =>
        FileApi.uploadFile(file, {
          userId: user?.userId || user?.id,
          documentTypeId: 4,
        }).then(async (response) => {
          if (!response.ok) {
            const errorText = await response.text().catch(() => '');
            throw new Error(`Upload failed for "${file.name}": ${response.status} ${errorText}`);
          }
          const uploadData = await response.json();
          const documentId =
            uploadData?.documentId ||
            uploadData?.document?.documentId ||
            uploadData?.document?.document_id ||
            uploadData?.id;
          if (!documentId) {
            throw new Error(`Upload succeeded for "${file.name}" but no document ID was returned.`);
          }
          return { file, documentId };
        })
      );

      const results = await Promise.all(uploadPromises);
      const newIds = results.map((r) => r.documentId);
      const newFiles = results.map((r) => ({
        name: r.file.name,
        size: r.file.size,
        type: r.file.type,
      }));

      const newScanIds = newIds.map((docId) =>
        syllabusScanService.startScan(docId, {})
      );

      const seen = new Set(newFiles.map((f) => f.name));
      const prevFiles = uploadedFiles.filter((f) => !seen.has(f.name));
      const prevIds = uploadedIds.filter((_, i) => !seen.has(uploadedFiles[i]?.name));
      const prevScanIds = (data.scanIds || []).filter((_, i) => !seen.has(uploadedFiles[i]?.name));

      const allIds = [...prevIds, ...newIds];
      const allFiles = [...prevFiles, ...newFiles];
      const allScanIds = [...prevScanIds, ...newScanIds];

      updateData({
        syllabusDocumentId: allIds[0],
        syllabusFile: allFiles[0],
        syllabusDocumentIds: allIds,
        syllabusFiles: allFiles,
        scanIds: allScanIds,
      });

      for (const { file, documentId } of results) {
        UsageEventsTracker.recordUserAction('ONBOARDING_SYLLABUS_UPLOADED', {
          action: 'upload',
          category: 'onboarding',
          entityType: 'document',
          entityId: documentId,
          metadata: { fileName: file.name, fileSize: file.size },
        });
      }
    } catch (err) {
      console.error('[SyllabusUploadStep] Upload failed:', err);
      setError(err.message || 'Upload failed. Please try again.');
      UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
        action: 'upload_failure',
        category: 'onboarding',
        label: 'syllabus-upload',
        metadata: { errorMessage: err.message || 'Upload failed' },
      });
    } finally {
      setUploading(false);
    }
  }, [user, updateData, uploadedIds, uploadedFiles, data.scanIds]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 10,
    multiple: true,
    noClick: false,
    noKeyboard: false,
  });

  // ── Manual form handlers (unchanged logic) ──
  const handleShowManualForm = () => {
    setShowManualForm(true);
    setManualCourseEntries([]);
    UsageEventsTracker.recordUserAction('ONBOARDING_NO_SYLLABUS_CLICKED', {
      action: 'click',
      category: 'onboarding',
      label: 'no_syllabus',
    });
  };

  const handleManualCourseAdd = (formData) => {
    setManualCourseEntries(prev => [...prev, formData]);
  };

  const handleManualFinish = (lastEntry) => {
    const entries = lastEntry ? [...manualCourseEntries, lastEntry] : manualCourseEntries;
    if (entries.length === 0) return;
    const isHybrid = uploadedFiles.length > 0;
    updateData({
      manualMode: true,
      manualCourseData: entries[0],
      manualCourseEntries: entries,
    });
    if (isHybrid) {
      goNext();
    } else {
      goToStep('course-review-0');
    }
  };

  const handleManualFormBack = () => {
    setShowManualForm(false);
    setManualCourseEntries([]);
  };

  // ── Join shared course handlers ──
  const handleShowJoinForm = () => {
    setShowJoinForm(true);
    setJoinCode('');
    setJoinError(null);
    setJoinPreview(null);
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'click',
      category: 'onboarding',
      label: 'join_shared_course',
    });
  };

  const handleJoinValidate = async () => {
    if (!joinCode.trim()) return;
    setJoinError(null);
    try {
      const resp = await InviteApi.validateInvite(joinCode.trim().toUpperCase());
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        setJoinError(text || 'Invalid or expired invite code.');
        return;
      }
      setJoinPreview(await resp.json());
    } catch (err) {
      setJoinError(err.message || 'Failed to validate invite code.');
    }
  };

  const handleJoinRedeem = async () => {
    setJoining(true);
    setJoinError(null);
    try {
      const resp = await InviteApi.redeemInvite(joinCode.trim().toUpperCase());
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        setJoinError(text || 'Failed to join course.');
        setJoining(false);
        return;
      }
      UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
        action: 'join_course_success',
        category: 'onboarding',
        label: 'join_shared_course',
      });
      // Skip to ai-tools or complete since the course is already set up
      updateData({ joinedSharedCourse: true });
      goNext();
    } catch (err) {
      setJoinError(err.message || 'Failed to join course.');
      setJoining(false);
    }
  };

  const handleJoinFormBack = () => {
    setShowJoinForm(false);
    setJoinCode('');
    setJoinError(null);
    setJoinPreview(null);
  };

  const handleRemoveFile = (index) => {
    const nextFiles = uploadedFiles.filter((_, i) => i !== index);
    const nextIds = uploadedIds.filter((_, i) => i !== index);
    const nextScanIds = (data.scanIds || []).filter((_, i) => i !== index);
    updateData({
      syllabusFile: nextFiles[0] || null,
      syllabusDocumentId: nextIds[0] || null,
      syllabusFiles: nextFiles,
      syllabusDocumentIds: nextIds,
      scanIds: nextScanIds,
    });
    if (nextFiles.length === 0) {
      setConfirmedDone(false);
      setAutoAdvancing(false);
      clearTimeout(advanceTimerRef.current);
    }
  };

  // ── Manual form view ──
  if (showManualForm) {
    return (
      <ManualCourseForm
        onSubmit={handleManualCourseAdd}
        onFinish={handleManualFinish}
        onBack={handleManualFormBack}
        courseCount={hasUploads ? Math.max(courseCount - uploadedFiles.length, 0) : courseCount}
        addedCourses={manualCourseEntries}
        onJoinedSharedCourse={() => {
          updateData({ joinedSharedCourse: true });
          goNext();
        }}
      />
    );
  }

  // ── Join shared course view ──
  if (showJoinForm) {
    return (
      <div className="onboarding-card ob-upload-card ob-manual-card">
        <div className="onboarding-card-header ob-upload-header">
          <h2>Join a shared course</h2>
          <p className="ob-upload-subtitle">
            Enter the invite code shared by your instructor or classmate.
          </p>
        </div>
        <div className="ob-manual-form">
          <div className="ob-manual-row">
            <div className="ob-manual-field">
              <label className="ob-manual-label" htmlFor="ob-join-code">Invite Code</label>
              <input
                id="ob-join-code"
                type="text"
                className="ob-manual-input"
                placeholder="e.g. ABC12345"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && !joinPreview && handleJoinValidate()}
                maxLength={20}
                autoFocus
                style={{ letterSpacing: '2px', fontWeight: 600, textAlign: 'center' }}
              />
            </div>
          </div>
          {joinError && <p style={{ color: '#ef4444', fontSize: '0.85rem', margin: '0.5rem 0' }}>{joinError}</p>}
          {joinPreview && (
            <div style={{ padding: '0.75rem', background: 'var(--card-bg)', border: '1px solid var(--border-default)', borderRadius: '10px', margin: '0.75rem 0' }}>
              <h4 style={{ margin: '0 0 0.25rem 0' }}>{joinPreview.endeavorName || joinPreview.courseName || 'Course'}</h4>
              {joinPreview.instructor && <p style={{ margin: '0.15rem 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Instructor: {joinPreview.instructor}</p>}
              {joinPreview.enrollmentCount != null && <p style={{ margin: '0.15rem 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{joinPreview.enrollmentCount} students enrolled</p>}
            </div>
          )}
        </div>
        <div className="onboarding-actions ob-upload-actions">
          <button className="onboarding-btn secondary" onClick={handleJoinFormBack}>← Back</button>
          <div className="ob-upload-actions-right">
            {!joinPreview ? (
              <button className={`onboarding-btn primary${joinCode.trim() ? '' : ' ob-btn--muted'}`} onClick={handleJoinValidate} disabled={!joinCode.trim()}>Validate Code</button>
            ) : (
              <button className={`onboarding-btn primary${joining ? ' ob-btn--muted' : ''}`} onClick={handleJoinRedeem} disabled={joining}>{joining ? 'Joining...' : 'Join Course →'}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Scout-driven upload view ──
  const isProcessing = uploading || autoAdvancing;

  return (
    <div className="syl-upload-wrapper">
      {/* Background effects */}
      <div className="syl-upload-bg-glow" aria-hidden="true" />
      <div className="syl-upload-bg-gradient" aria-hidden="true" />

      {/* Floating particles */}
      {PARTICLES.map((p) => (
        <motion.div
          key={p.id}
          className="syl-upload-particle"
          aria-hidden="true"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -12, 0], opacity: [0.12, 0.45, 0.12] }}
          transition={{ duration: p.dur, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
        />
      ))}

      {/* Back link */}
      <motion.button
        className="syl-upload-back"
        onClick={goBack}
        type="button"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        whileHover={{ opacity: 0.85 }}
      >
        ← Back
      </motion.button>

      <div className="syl-upload-layout">
        {/* Left: Scout */}
        <motion.div
          className="syl-upload-left"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="syl-upload-scout-glow"
            aria-hidden="true"
            animate={{
              scale: isProcessing ? [1, 1.2, 1] : [1, 1.04, 1],
              opacity: isProcessing ? [0.65, 0.95, 0.65] : [0.6, 0.75, 0.6],
            }}
            transition={isProcessing
              ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 5, repeat: Infinity, ease: 'easeInOut' }
            }
          />
          <motion.img
            src="/scout_images/scout_thinking.png"
            alt="Scout organizing"
            className="syl-upload-scout-img"
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="syl-upload-light-spill" aria-hidden="true" />

          {/* Scout tagline */}
          {!isProcessing && (
            <motion.p
              className="syl-upload-scout-tagline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              Drop them in. I'll handle it.
            </motion.p>
          )}

          {/* System messages (below Scout) */}
          <AnimatePresence mode="wait">
            {isProcessing && (
              <motion.p
                key={systemMsgIdx}
                className="syl-upload-system-msg"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3 }}
              >
                {SYSTEM_MESSAGES[systemMsgIdx]}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Right: Upload interaction */}
        <div className="syl-upload-right">
          <motion.h2
            className="syl-upload-headline"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
          >
            Let's <span className="syl-upload-headline-glow">build</span> your <span className="syl-upload-headline-glow">semester</span>
          </motion.h2>

          <motion.p
            className="syl-upload-subtitle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            Drop your syllabi and I'll organize everything for you.
          </motion.p>

          {/* Upload zone */}
          <motion.div
            {...getRootProps()}
            className={`syl-upload-zone${isDragActive ? ' syl-upload-zone--drag' : ''}${uploading ? ' syl-upload-zone--uploading' : ''}${hasUploads ? ' syl-upload-zone--has-files' : ''}`}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            whileHover={!uploading ? { scale: 1.01 } : undefined}
          >
            <input {...getInputProps()} />
            <div className="syl-upload-zone-glow" aria-hidden="true" />

            {uploading ? (
              <div className="syl-upload-zone-content">
                <div className="syl-upload-spinner" aria-hidden="true" />
                <span className="syl-upload-zone-text">Uploading…</span>
              </div>
            ) : isDragActive ? (
              <div className="syl-upload-zone-content">
                <UploadIcon />
                <span className="syl-upload-zone-text">Drop files to upload</span>
              </div>
            ) : (
              <div className="syl-upload-zone-content">
                <UploadIcon />
                <span className="syl-upload-zone-text">
                  {hasUploads ? 'Drop more syllabi' : 'Drop your syllabi here'}
                </span>
                <span className="syl-upload-zone-hint">or click to upload · PDF format works best</span>
              </div>
            )}
          </motion.div>

          {/* Uploaded files list */}
          <AnimatePresence>
            {hasUploads && (
              <motion.ul
                className="syl-upload-file-list"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {uploadedFiles.map((file, idx) => (
                  <motion.li
                    key={file.name}
                    className="syl-upload-file-item"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.28, delay: idx * 0.06, ease: 'easeOut' }}
                  >
                    <FileIcon />
                    <span className="syl-upload-file-name">{file.name}</span>
                    <motion.span
                      className="syl-upload-file-ready"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.4 + idx * 0.06, type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      Ready
                    </motion.span>
                    <button
                      type="button"
                      className="syl-upload-file-remove"
                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(idx); }}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                className="syl-upload-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Mismatch prompt — Scout asks if they have another or want to continue */}
          <AnimatePresence>
            {hasMismatch && !uploading && !autoAdvancing && (
              <motion.div
                className="syl-upload-mismatch"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35, delay: 0.2 }}
              >
                <p className="syl-upload-mismatch-text">
                  You mentioned <strong>{courseCount}</strong> {courseCount === 1 ? 'class' : 'classes'} but
                  uploaded <strong>{uploadedFiles.length}</strong> {uploadedFiles.length === 1 ? 'syllabus' : 'syllabi'}.
                  Have another to add?
                </p>
                <div className="syl-upload-mismatch-actions">
                  <motion.button
                    type="button"
                    className="syl-upload-continue"
                    onClick={goNext}
                    whileTap={{ scale: 0.97 }}
                  >
                    That's all — continue →
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Manual fallback link — only when no uploads yet */}
          {!hasUploads && !uploading && (
            <div className="syl-upload-alt-links">
              <motion.button
                type="button"
                className="syl-upload-manual-link"
                onClick={handleShowManualForm}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
              >
                Don't have them? <span>Create your courses manually</span>
              </motion.button>
              <motion.button
                type="button"
                className="syl-upload-manual-link"
                onClick={handleShowJoinForm}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                Have an invite code? <span>Join a shared course</span>
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
